import { useAuth, API } from "@/App";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  User, Mail, Calendar, Shield, Copy, CheckCircle, 
  Camera, Trash2, Loader2, Phone, MapPin, Edit3, Save, X 
} from "lucide-react";
import { toast } from "sonner";
import { useState, useRef } from "react";
import axios from "axios";
import DrishtiSubscriptionCard from "@/components/drishti/DrishtiSubscriptionCard";

const ProfilePage = () => {
  const { user, setUser, checkAuth, logoutUrl } = useAuth();
  const [copied, setCopied] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isEditingDetails, setIsEditingDetails] = useState(false);
  const [isSavingDetails, setIsSavingDetails] = useState(false);

  // Form states for basic account edit
  const [editName, setEditName] = useState(user?.name || "");
  const [editPhone, setEditPhone] = useState(user?.phone || "");
  const [editCity, setEditCity] = useState(user?.city || "");

  const fileInputRef = useRef(null);

  const copyReferralCode = () => {
    if (user?.referral_code) {
      navigator.clipboard.writeText(user.referral_code);
      setCopied(true);
      toast.success("Referral code copied!");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleStartEdit = () => {
    setEditName(user?.name || "");
    setEditPhone(user?.phone || "");
    setEditCity(user?.city || "");
    setIsEditingDetails(true);
  };

  const handleSaveDetails = async () => {
    if (!editName.trim()) {
      toast.error("Name cannot be empty");
      return;
    }
    setIsSavingDetails(true);
    try {
      const res = await axios.put(
        `${API}/user/profile`,
        {
          name: editName.trim(),
          phone: editPhone.trim(),
          city: editCity.trim()
        },
        { withCredentials: true }
      );
      if (res.data) {
        setUser((prev) => ({ ...prev, ...res.data }));
        if (checkAuth) checkAuth();
        toast.success("Profile details updated!");
        setIsEditingDetails(false);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update profile details");
    } finally {
      setIsSavingDetails(false);
    }
  };

  // Process & upload image
  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate type
    if (!file.type.startsWith("image/")) {
      toast.error("Please select a valid image file (PNG, JPG, WebP)");
      return;
    }

    // Validate size (< 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image file size should be less than 10MB");
      return;
    }

    setIsUploading(true);
    try {
      // Resize & compress to max 500x500 square via canvas for optimal speed & quality
      const compressedDataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (event) => {
          const img = new Image();
          img.onload = () => {
            const canvas = document.createElement("canvas");
            const maxDim = 500;
            let width = img.width;
            let height = img.height;

            // Crop to square aspect ratio
            const minDim = Math.min(width, height);
            const startX = (width - minDim) / 2;
            const startY = (height - minDim) / 2;

            canvas.width = Math.min(minDim, maxDim);
            canvas.height = Math.min(minDim, maxDim);

            const ctx = canvas.getContext("2d");
            ctx.drawImage(img, startX, startY, minDim, minDim, 0, 0, canvas.width, canvas.height);
            resolve(canvas.toDataURL("image/jpeg", 0.88));
          };
          img.onerror = reject;
          img.src = event.target.result;
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      // Send to server
      const res = await axios.put(
        `${API}/user/profile`,
        { picture: compressedDataUrl },
        { withCredentials: true }
      );

      if (res.data) {
        setUser((prev) => ({ ...prev, picture: res.data.picture || compressedDataUrl }));
        if (checkAuth) checkAuth();
        toast.success("Profile picture updated successfully!");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update profile picture");
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleRemovePicture = async () => {
    if (!user?.picture) return;
    setIsUploading(true);
    try {
      const res = await axios.put(
        `${API}/user/profile`,
        { picture: "" },
        { withCredentials: true }
      );
      if (res.data) {
        setUser((prev) => ({ ...prev, picture: null }));
        if (checkAuth) checkAuth();
        toast.success("Profile picture removed");
      }
    } catch (err) {
      toast.error("Failed to remove profile picture");
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <DashboardLayout title="Profile">
      <div className="max-w-3xl mx-auto space-y-6" data-testid="profile-page">
        {/* Hidden File Input for Avatar Upload */}
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          accept="image/png, image/jpeg, image/webp, image/gif"
          className="hidden"
          data-testid="profile-pic-input"
        />

        {/* Profile Card */}
        <Card className="bg-white dark:bg-[#131B2C] border-gray-200 dark:border-[#1E293B]" data-testid="profile-card">
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6">
              {/* Avatar with Camera Trigger Overlay */}
              <div className="relative group shrink-0">
                <Avatar className="w-24 h-24 sm:w-28 sm:h-28 border-2 border-[#00d991] shadow-lg overflow-hidden">
                  <AvatarImage src={user?.picture} alt={user?.name} className="object-cover w-full h-full" />
                  <AvatarFallback className="bg-gradient-to-br from-[#1A243A] to-[#0A0E17] text-white text-3xl font-bold font-outfit">
                    {user?.name?.charAt(0)?.toUpperCase() || "U"}
                  </AvatarFallback>
                </Avatar>

                {/* Upload Hover Overlay */}
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploading}
                  className="absolute inset-0 rounded-full bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center text-white cursor-pointer disabled:cursor-not-allowed"
                  title="Upload profile picture"
                  data-testid="avatar-hover-upload-btn"
                >
                  {isUploading ? (
                    <Loader2 className="w-6 h-6 animate-spin text-[#00d991]" />
                  ) : (
                    <>
                      <Camera className="w-6 h-6 text-[#00d991]" />
                      <span className="text-[10px] font-semibold mt-1">Change</span>
                    </>
                  )}
                </button>

                {/* Camera Mini-Badge Button */}
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploading}
                  className="absolute bottom-0 right-0 w-8 h-8 rounded-full bg-[#00d991] text-black hover:bg-[#00c080] flex items-center justify-center shadow-md border-2 border-white dark:border-[#131B2C] transition-transform active:scale-95 disabled:opacity-50 cursor-pointer"
                  title="Upload photo"
                  data-testid="avatar-badge-upload-btn"
                >
                  {isUploading ? (
                    <Loader2 className="w-4 h-4 animate-spin text-black" />
                  ) : (
                    <Camera className="w-4 h-4 text-black" />
                  )}
                </button>
              </div>

              {/* User Bio & Actions */}
              <div className="text-center sm:text-left flex-1 min-w-0">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white font-outfit truncate">
                      {user?.name}
                    </h1>
                    <p className="text-sm text-gray-500 dark:text-[#94A3B8] truncate">{user?.email}</p>
                  </div>

                  <div className="flex items-center justify-center sm:justify-start gap-2 mt-1 sm:mt-0">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isUploading}
                      className="h-8 text-xs border-gray-200 dark:border-white/[0.1] text-gray-700 dark:text-zinc-200 hover:text-gray-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-white/10"
                      data-testid="upload-photo-btn"
                    >
                      <Camera className="w-3.5 h-3.5 mr-1.5 text-[#00d991]" />
                      {user?.picture ? "Change Photo" : "Upload Photo"}
                    </Button>

                    {user?.picture && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleRemovePicture}
                        disabled={isUploading}
                        className="h-8 px-2 text-xs text-red-500 hover:text-red-600 hover:bg-red-500/10"
                        title="Remove photo"
                        data-testid="remove-photo-btn"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                </div>

                <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2 mt-3">
                  <Badge className="bg-[#00d991]/20 text-[#00d991] capitalize font-mono text-xs">
                    <Shield className="w-3 h-3 mr-1" />
                    {user?.role}
                  </Badge>
                  <Badge className="bg-[#2563EB]/20 text-[#2563EB] text-xs">
                    Active Member
                  </Badge>
                  {user?.city && (
                    <Badge variant="outline" className="border-gray-200 dark:border-white/[0.1] text-gray-600 dark:text-zinc-300 text-xs">
                      <MapPin className="w-3 h-3 mr-1 text-[#00F0FF]" />
                      {user.city}
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Drishti AI AutoPay Subscription Management */}
        <DrishtiSubscriptionCard />

        {/* Account Details */}
        <Card className="bg-white dark:bg-[#131B2C] border-gray-200 dark:border-[#1E293B]" data-testid="account-details">
          <CardHeader className="flex flex-row items-center justify-between pb-3">
            <CardTitle className="text-gray-900 dark:text-white font-outfit text-lg">Account Details</CardTitle>
            {!isEditingDetails ? (
              <Button
                variant="outline"
                size="sm"
                onClick={handleStartEdit}
                className="h-8 text-xs border-gray-200 dark:border-white/[0.1] text-gray-700 dark:text-zinc-300"
                data-testid="edit-details-btn"
              >
                <Edit3 className="w-3.5 h-3.5 mr-1 text-[#00F0FF]" />
                Edit Info
              </Button>
            ) : (
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsEditingDetails(false)}
                  disabled={isSavingDetails}
                  className="h-8 text-xs text-gray-500 hover:text-gray-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-white/10"
                >
                  <X className="w-3.5 h-3.5 mr-1" />
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={handleSaveDetails}
                  disabled={isSavingDetails}
                  className="h-8 text-xs bg-[#00d991] hover:bg-[#00c080] text-black font-semibold"
                  data-testid="save-details-btn"
                >
                  {isSavingDetails ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin mr-1 text-black" />
                  ) : (
                    <Save className="w-3.5 h-3.5 mr-1 text-black" />
                  )}
                  Save
                </Button>
              </div>
            )}
          </CardHeader>
          <CardContent className="space-y-3">
            {/* Full Name */}
            <div className="flex items-center justify-between p-3.5 bg-gray-50 dark:bg-[#0A0E17] rounded-xl border border-gray-100 dark:border-white/[0.04]">
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <div className="w-9 h-9 rounded-lg bg-[#2563EB]/10 flex items-center justify-center shrink-0">
                  <User className="w-4 h-4 text-[#2563EB]" strokeWidth={2} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] uppercase font-mono tracking-wider text-gray-400 dark:text-[#64748B]">Full Name</p>
                  {isEditingDetails ? (
                    <Input
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      className="h-8 mt-1 text-sm bg-white dark:bg-[#131B2C] border-gray-200 dark:border-white/[0.1] text-gray-900 dark:text-white"
                      placeholder="Your full name"
                    />
                  ) : (
                    <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">{user?.name}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Email Address */}
            <div className="flex items-center justify-between p-3.5 bg-gray-50 dark:bg-[#0A0E17] rounded-xl border border-gray-100 dark:border-white/[0.04]">
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <div className="w-9 h-9 rounded-lg bg-[#00d991]/10 flex items-center justify-center shrink-0">
                  <Mail className="w-4 h-4 text-[#00d991]" strokeWidth={2} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] uppercase font-mono tracking-wider text-gray-400 dark:text-[#64748B]">Email Address</p>
                  <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">{user?.email}</p>
                </div>
              </div>
            </div>

            {/* Phone */}
            <div className="flex items-center justify-between p-3.5 bg-gray-50 dark:bg-[#0A0E17] rounded-xl border border-gray-100 dark:border-white/[0.04]">
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <div className="w-9 h-9 rounded-lg bg-[#F59E0B]/10 flex items-center justify-center shrink-0">
                  <Phone className="w-4 h-4 text-[#F59E0B]" strokeWidth={2} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] uppercase font-mono tracking-wider text-gray-400 dark:text-[#64748B]">Phone Number</p>
                  {isEditingDetails ? (
                    <Input
                      value={editPhone}
                      onChange={(e) => setEditPhone(e.target.value)}
                      className="h-8 mt-1 text-sm bg-white dark:bg-[#131B2C] border-gray-200 dark:border-white/[0.1] text-gray-900 dark:text-white font-mono"
                      placeholder="+91 98765 43210"
                    />
                  ) : (
                    <p className="text-sm font-semibold text-gray-900 dark:text-white font-mono truncate">{user?.phone || "—"}</p>
                  )}
                </div>
              </div>
            </div>

            {/* City */}
            <div className="flex items-center justify-between p-3.5 bg-gray-50 dark:bg-[#0A0E17] rounded-xl border border-gray-100 dark:border-white/[0.04]">
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <div className="w-9 h-9 rounded-lg bg-[#00F0FF]/10 flex items-center justify-center shrink-0">
                  <MapPin className="w-4 h-4 text-[#00F0FF]" strokeWidth={2} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] uppercase font-mono tracking-wider text-gray-400 dark:text-[#64748B]">City</p>
                  {isEditingDetails ? (
                    <Input
                      value={editCity}
                      onChange={(e) => setEditCity(e.target.value)}
                      className="h-8 mt-1 text-sm bg-white dark:bg-[#131B2C] border-gray-200 dark:border-white/[0.1] text-gray-900 dark:text-white"
                      placeholder="e.g. Jaipur, Bundi, Mumbai"
                    />
                  ) : (
                    <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">{user?.city || "—"}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Member Since & User ID */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
              <div className="flex items-center gap-3 p-3.5 bg-gray-50 dark:bg-[#0A0E17] rounded-xl border border-gray-100 dark:border-white/[0.04]">
                <div className="w-9 h-9 rounded-lg bg-purple-500/10 flex items-center justify-center shrink-0">
                  <Calendar className="w-4 h-4 text-purple-400" strokeWidth={2} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] uppercase font-mono tracking-wider text-gray-400 dark:text-[#64748B]">Member Since</p>
                  <p className="text-xs font-semibold text-gray-900 dark:text-white truncate">
                    {user?.created_at ? new Date(user.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—"}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3 p-3.5 bg-gray-50 dark:bg-[#0A0E17] rounded-xl border border-gray-100 dark:border-white/[0.04]">
                <div className="w-9 h-9 rounded-lg bg-emerald-500/10 flex items-center justify-center shrink-0">
                  <Shield className="w-4 h-4 text-emerald-400" strokeWidth={2} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] uppercase font-mono tracking-wider text-gray-400 dark:text-[#64748B]">User ID</p>
                  <p className="text-[11px] font-mono font-semibold text-gray-900 dark:text-white truncate">{user?.user_id}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Referral Code */}
        <Card className="bg-white dark:bg-[#131B2C] border-gray-200 dark:border-[#1E293B]" data-testid="referral-section">
          <CardHeader>
            <CardTitle className="text-gray-900 dark:text-white font-outfit text-lg">Your Referral Code</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-[#0A0E17] rounded-xl border border-[#00d991]/30">
              <code className="text-xl font-mono font-bold text-[#00d991]">
                {user?.referral_code || "N/A"}
              </code>
              <Button
                variant="ghost"
                size="sm"
                onClick={copyReferralCode}
                className="text-gray-500 dark:text-[#94A3B8] hover:text-gray-900 dark:text-white hover:bg-white/[0.05]"
                data-testid="copy-referral-btn"
              >
                {copied ? (
                  <CheckCircle className="w-5 h-5 text-[#10B981]" />
                ) : (
                  <Copy className="w-5 h-5" />
                )}
              </Button>
            </div>
            <p className="text-xs text-gray-500 dark:text-[#64748B] mt-2">
              Share this code with friends to earn rewards when they join JamaDhan!
            </p>
          </CardContent>
        </Card>

        {/* Actions */}
        <Card className="bg-white dark:bg-[#131B2C] border-gray-200 dark:border-[#1E293B]" data-testid="actions-section">
          <CardHeader>
            <CardTitle className="text-gray-900 dark:text-white font-outfit text-lg">Account Actions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button
              variant="outline"
              className="w-full justify-start border-gray-200 dark:border-[#1E293B] text-gray-500 dark:text-[#94A3B8] hover:text-gray-900 dark:text-white hover:bg-[#1A243A]"
              onClick={() => window.open("mailto:support@jamadhan.in", "_blank")}
              data-testid="contact-support-btn"
            >
              Contact Support
            </Button>
            <Button
              asChild
              variant="outline"
              className="w-full justify-start border-[#EF4444]/30 text-[#EF4444] hover:bg-[#EF4444]/10"
              data-testid="logout-btn"
            >
              <a href={logoutUrl}>Sign Out</a>
            </Button>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default ProfilePage;
